import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Alert, Spinner } from 'react-bootstrap';
import { Icon } from '@iconify/react';
import { API_URL } from '../../config/api';

const BlingAccountManager = () => {
  const [conta, setConta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    nome: 'Minha Conta Bling',
    client_id: '',
    client_secret: ''
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [validating, setValidating] = useState(false);

  useEffect(() => {
    loadConta();
  }, []);

  const loadConta = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('auth_token') || localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/bling/conta-unica`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const result = await response.json();
        if (result.exists) {
          setConta(result.data);
        }
      } else {
        console.error('Erro ao carregar conta:', response.status);
      }
    } catch (error) {
      console.error('Erro ao carregar conta:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setValidating(true);
      setError('');
      setSuccess('');

      if (!formData.nome || !formData.client_id || !formData.client_secret) {
        setError('Todos os campos são obrigatórios');
        return;
      }

      const token = localStorage.getItem('auth_token') || localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/bling/conta-unica`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      const result = await response.json();

      if (response.ok) {
        setSuccess(result.message);
        setConta(result.data);
        setShowModal(false);
        setFormData({ nome: 'Minha Conta Bling', client_id: '', client_secret: '' });
      } else {
        setError(result.message || 'Erro ao salvar conta');
      }
    } catch (error) {
      console.error('Erro ao salvar conta:', error);
      setError('Erro ao salvar conta: ' + error.message);
    } finally {
      setValidating(false);
    }
  };

  const handleAuthorize = () => {
    if (!conta) return;

    // DEBUG: Mostrar informações
    console.log('🔗 Bling OAuth - Client ID:', conta.client_id);
    console.log('🔗 Bling OAuth - State (Conta ID):', conta.id);

    // Bling usa o redirect_uri configurado no painel, não enviar na URL
    const params = new URLSearchParams({
      response_type: 'code',
      client_id: conta.client_id,
      state: conta.id.toString()
    });

    const authUrl = `https://www.bling.com.br/Api/v3/oauth/authorize?${params.toString()}`;

    console.log('🔗 Bling OAuth - URL completa:', authUrl);

    // Abrir popup e monitorar fechamento
    const popup = window.open(authUrl, 'bling_auth', 'width=600,height=700,scrollbars=yes,resizable=yes');

    // Monitorar fechamento do popup
    const checkClosed = setInterval(() => {
      if (popup.closed) {
        clearInterval(checkClosed);
        // Recarregar dados da conta após autorização
        setTimeout(() => {
          loadConta();
        }, 1000);
      }
    }, 1000);
  };

  const getStatusBadge = (status) => {
    const badges = {
      'ativa': { bg: 'success', text: 'Autorizada', icon: 'mdi:check-circle' },
      'pendente': { bg: 'warning', text: 'Pendente', icon: 'mdi:clock-outline' },
      'erro': { bg: 'danger', text: 'Erro', icon: 'mdi:alert-circle' }
    };

    const badge = badges[status] || badges['pendente'];
    
    return (
      <span className={`badge bg-${badge.bg} d-flex align-items-center gap-1`}>
        <Icon icon={badge.icon} width={14} />
        {badge.text}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="text-center py-4">
        <Spinner animation="border" size="sm" />
        <p className="mt-2 mb-0">Carregando conta Bling...</p>
      </div>
    );
  }

  return (
    <div className="bling-account-manager">
      {!conta && (
        <div className="d-flex justify-content-end mb-3">
          <Button 
            variant="primary" 
            size="sm"
            onClick={() => setShowModal(true)}
          >
            <Icon icon="mdi:plus" className="me-1" />
            Configurar Conta
          </Button>
        </div>
      )}

      {success && <Alert variant="success" dismissible onClose={() => setSuccess('')}>{success}</Alert>}

      {conta ? (
        <div className="card">
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-start mb-3">
              <div>
                <h6 className="card-title mb-1">{conta.nome}</h6>
                <small className="text-muted">Client ID: {conta.client_id}</small>
              </div>
              {getStatusBadge(conta.status)}
            </div>

            <div className="d-flex gap-2">
              <Button
                variant={conta.status === 'ativa' ? 'outline-success' : 'success'}
                size="sm"
                onClick={handleAuthorize}
              >
                <Icon icon="mdi:key" className="me-1" />
                {conta.status === 'ativa' ? 'Re-autorizar' : 'Autorizar Conta'}
              </Button>

              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => {
                  setFormData({
                    nome: conta.nome,
                    client_id: conta.client_id,
                    client_secret: ''
                  });
                  setShowModal(true);
                }}
              >
                <Icon icon="mdi:pencil" className="me-1" />
                Editar
              </Button>
            </div>

            {conta.ultima_sincronizacao && (
              <div className="mt-3 pt-3 border-top">
                <small className="text-muted">
                  <Icon icon="mdi:sync" className="me-1" />
                  Última sincronização: {new Date(conta.ultima_sincronizacao).toLocaleString()}
                </small>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="text-center py-4 border rounded bg-light">
          <Icon icon="mdi:account-plus" width={48} className="text-muted mb-2" />
          <p className="mb-2">Nenhuma conta Bling configurada</p>
          <p className="text-muted small mb-3">Configure sua conta Bling para sincronizar vendas automaticamente</p>
          <Button variant="primary" onClick={() => setShowModal(true)}>
            Configurar Conta Bling
          </Button>
        </div>
      )}

      {/* Modal de Configuração */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <Icon icon="mdi:cog" className="me-2" />
            {conta ? 'Editar Conta Bling' : 'Configurar Conta Bling'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && <Alert variant="danger">{error}</Alert>}

          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Nome da Conta <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="text"
                value={formData.nome}
                onChange={(e) => setFormData({...formData, nome: e.target.value})}
                placeholder="Ex: Minha Conta Bling"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Client ID <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="text"
                value={formData.client_id}
                onChange={(e) => setFormData({...formData, client_id: e.target.value})}
                placeholder="Client ID da sua aplicação Bling"
              />
              <Form.Text className="text-muted">
                Encontre no painel de desenvolvedor da Bling
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Client Secret <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="password"
                value={formData.client_secret}
                onChange={(e) => setFormData({...formData, client_secret: e.target.value})}
                placeholder="Client Secret da sua aplicação Bling"
              />
              <Form.Text className="text-muted">
                Mantenha em segurança - nunca compartilhe
              </Form.Text>
            </Form.Group>
          </Form>

          {/* URL de Redirecionamento - IMPORTANTE */}
          <Alert variant="warning" className="mb-3">
            <div className="d-flex align-items-start">
              <Icon icon="mdi:alert" className="me-2 mt-1" style={{ fontSize: '20px' }} />
              <div style={{ flex: 1 }}>
                <strong>URL de Redirecionamento (obrigatório):</strong>
                <p className="mb-2 mt-1">
                  Copie esta URL e cole no campo <strong>"URL de Redirecionamento"</strong> do seu aplicativo no painel Bling:
                </p>
                <div className="d-flex align-items-center gap-2">
                  <code
                    className="bg-dark text-light px-3 py-2 rounded d-block"
                    style={{ fontSize: '13px', wordBreak: 'break-all', flex: 1 }}
                  >
                    {window.location.origin}/bling-callback.html
                  </code>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => {
                      navigator.clipboard.writeText(`${window.location.origin}/bling-callback.html`);
                      alert('URL copiada!');
                    }}
                    title="Copiar URL"
                  >
                    <Icon icon="mdi:content-copy" />
                  </Button>
                </div>
              </div>
            </div>
          </Alert>

          <Alert variant="info">
            <Icon icon="mdi:information" className="me-2" />
            <strong>Como configurar:</strong>
            <ol className="mb-0 mt-2">
              <li>Acesse <a href="https://developer.bling.com.br/aplicativos" target="_blank" rel="noopener noreferrer">developer.bling.com.br/aplicativos</a></li>
              <li>Crie ou edite sua aplicação</li>
              <li><strong>Cole a URL de redirecionamento acima</strong></li>
              <li>Copie o Client ID e Client Secret</li>
              <li>Cole aqui e salve</li>
            </ol>
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Cancelar
          </Button>
          <Button 
            variant="primary" 
            onClick={handleSave}
            disabled={validating}
          >
            {validating ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Validando...
              </>
            ) : (
              <>
                <Icon icon="mdi:check" className="me-1" />
                Salvar
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default BlingAccountManager;
